import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import useAuth from "@/hooks/useAuth";
import { redactUrl } from "@/shared/services/diagnostic/redactUrl";
import {
  CalendarV2Event,
  isCalendarV2EventVisible,
} from "@/shared/models/economic-calendar-v2/CalendarV2Event";
import { dayBoundsIso } from ".";

const BASE_URL = import.meta.env.VITE_CALENDAR_V2_BASE_URL;

// Named SSE events emitted by GET /api/subscribe/calendar, one per
// classification. `ready`/`deleted` are handled separately.
const CLASSIFICATION_EVENTS = [
  "data",
  "speaker",
  "supply",
  "earning",
  "expiry",
  "holiday",
  "ad_hoc",
] as const;

const localDateKey = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};

/**
 * Dedicated, day-scoped SSE for the new calendar backend.
 *
 * This is a self-contained native EventSource (not the shared SSEClientManager,
 * which only routes the default `message` event — the calendar backend emits
 * *named* events per classification). The Keycloak token rides as `?token=`
 * because EventSource cannot set an Authorization header. The connection is
 * recreated whenever the access token changes so a refreshed token replaces a
 * stale one instead of 401-looping. Token is redacted before any logging.
 *
 * When the datahose eventually carries calendar-v2 events, this hook can be
 * deleted in favour of the shared equitiesSSEClient piggyback.
 */
export const useCalendarV2LiveFeed = (
  day: Date,
  dateKey: string,
  enabled: boolean
) => {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  // Bound the SSE to the same local day as the REST snapshot. Recompute the
  // ISO bounds only when the day-key changes (not on every Date identity).
  const { occurs_from, occurs_to } = dayBoundsIso(day);
  const boundsRef = useRef({ occurs_from, occurs_to });
  boundsRef.current = { occurs_from, occurs_to };

  useEffect(() => {
    if (!enabled || !accessToken) return;

    const upsert = (event: CalendarV2Event) => {
      if (!event?.uuid) return;
      const key = ["calendarV2Events", dateKey];

      const removeByUuid = (list: CalendarV2Event[]) => {
        const idx = list.findIndex((e) => e.uuid === event.uuid);
        if (idx < 0) return list;
        const next = list.slice();
        next.splice(idx, 1);
        return next;
      };

      queryClient.setQueryData(
        key,
        (old: CalendarV2Event[] | undefined) => {
          const list = old ?? [];

          // Dropped events (deleted category visibility, disabled) leave the day.
          if (!isCalendarV2EventVisible(event)) return removeByUuid(list);

          // Event moved to another day — remove it from this one if present.
          if (event.occurs_at && localDateKey(event.occurs_at) !== dateKey) {
            return removeByUuid(list);
          }

          const idx = list.findIndex((e) => e.uuid === event.uuid);
          if (idx >= 0) {
            const next = list.slice();
            next[idx] = { ...list[idx], ...event } as CalendarV2Event;
            return next;
          }
          return [...list, event];
        }
      );
    };

    const remove = (uuid: string) => {
      queryClient.setQueryData(
        ["calendarV2Events", dateKey],
        (old: CalendarV2Event[] | undefined) =>
          (old ?? []).filter((e) => e.uuid !== uuid)
      );
    };

    const url = new URL("/api/subscribe/calendar", BASE_URL);
    url.searchParams.set("token", accessToken);
    url.searchParams.set("occurs_from", boundsRef.current.occurs_from);
    url.searchParams.set("occurs_to", boundsRef.current.occurs_to);

    const es = new EventSource(url.toString());
    let connectedBefore = false;

    const onClassification = (ev: MessageEvent) => {
      if (!ev.data) return;
      try {
        upsert(JSON.parse(ev.data) as CalendarV2Event);
      } catch (error) {
        console.error("calendar-v2 SSE parse error", error);
        Sentry.captureException(error, {
          extra: { connectionName: "calendar-v2:sse" },
        });
      }
    };

    CLASSIFICATION_EVENTS.forEach((name) =>
      es.addEventListener(name, onClassification as EventListener)
    );

    es.addEventListener("deleted", (ev: MessageEvent) => {
      if (!ev.data) return;
      try {
        const payload = JSON.parse(ev.data) as { uuid?: string };
        if (payload?.uuid) remove(payload.uuid);
      } catch {
        /* ignore malformed delete payloads */
      }
    });

    es.addEventListener("ready", () => {
      // First ready = connected. A later ready means we reconnected after a
      // gap — refetch the day to backfill anything missed while disconnected.
      if (connectedBefore) {
        queryClient.invalidateQueries({ queryKey: ["calendarV2Events", dateKey] });
      }
      connectedBefore = true;
    });

    es.onerror = () => {
      // Native EventSource auto-reconnects with backoff. Nothing to do beyond
      // noting it; a token expiry is handled by the accessToken-keyed re-run.
      console.warn(
        `calendar-v2 SSE error on ${redactUrl(url.toString())}`
      );
    };

    return () => es.close();
  }, [enabled, accessToken, dateKey, queryClient]);
};
